import { FC, useRef } from 'react';

import type { IWallet } from '@entities/wallets';
import { useUpdateWalletMutation } from '@entities/wallets';
import { ModalEditEntity } from '@features/modal-entity-edit';
import type { ITagOption } from '@features/wallets';
import { tagToOption, useAssignWalletTags } from '@features/wallets';

interface IReadOnlyKeyConfig {
  tooltip?: string;
  hasCopyBtn?: boolean;
}

interface IProps {
  wallet: IWallet;
  excludeKeys: (keyof IWallet)[];
  readOnlyKeys?: Partial<Record<keyof IWallet, IReadOnlyKeyConfig>>;
  additionalKeys?: Partial<Record<keyof IWallet, string>>[];
  refetch: () => void;
}

/**
 * Edit-wallet wrapper around the generic ModalEditEntity. Owns the tags selection ref and the
 * post-save assign call — hooks can't live in `useTableSettings.renderItem`. Tags persist via a
 * separate `PUT /wallets/{id}/tags` (replace semantics), not the wallet PATCH payload.
 */
export const EditWallet: FC<IProps> = ({
  wallet,
  excludeKeys,
  readOnlyKeys,
  additionalKeys,
  refetch,
}) => {
  const isParentWallet = wallet.parent_id === null;
  const { assignTags } = useAssignWalletTags();

  const tagsRef = useRef<ITagOption[]>((wallet.tags ?? []).map(tagToOption));

  const handleEditWallet = (formData: FormData) => {
    if (!formData.get('login_credentials_json')) {
      formData.delete('login_credentials_json');
    }
    if (!formData.get('kind')) {
      formData.delete('kind');
    }
    // The form renders the read-model key `wallet_holder_name`, but the API writes the field as
    // `holder_name` (same wire name the create form uses) and ignores the read key.
    if (formData.has('wallet_holder_name')) {
      formData.set('holder_name', formData.get('wallet_holder_name'));
      formData.delete('wallet_holder_name');
    }
    return formData;
  };

  // Only parent wallets carry tags; assignWalletTags + the modal already refresh the list.
  const handleSuccess = isParentWallet
    ? async () => {
        await assignTags(wallet.id, tagsRef.current);
      }
    : undefined;

  // Guarantee the Tags field renders for an untagged parent wallet (BE may omit the key).
  const entity = isParentWallet ? { ...wallet, tags: wallet.tags ?? [] } : wallet;

  return (
    <ModalEditEntity
      title={`Edit Wallet ${wallet.name}`}
      entity={entity}
      excludeKeys={excludeKeys}
      readOnlyKeys={readOnlyKeys}
      additionalKeys={additionalKeys}
      useEditEntityMutation={useUpdateWalletMutation}
      onTransformFormData={handleEditWallet}
      onSuccess={handleSuccess}
      otherProps={{ tagsRef }}
      refetch={refetch}
    />
  );
};
